import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Grid,
  alpha,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Grow,
  Zoom,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Speed as SpeedIcon,
  VerifiedUser as VerifiedIcon,
  People as PeopleIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon,
  Shield as ShieldIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  MobileFriendly as MobileIcon,
  CloudDone as CloudIcon,
  Lock as LockIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Circle as CircleIcon, 
  ArrowBack as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  RocketLaunch as RocketLaunchIcon,
  Email as EmailIcon,
  SupportAgent as SupportAgentIcon,
  WebAsset as WebAssetIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Storefront as StorefrontIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingDown as TrendingDownIcon,
  LocalShipping as LocalShippingIcon,
  CarRental as CarRentalIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

// Stats data for car inventory management
const stats = [
  { value: '500+', label: 'Vehicles Managed', icon: <CarRentalIcon />, color: '#2196F3' },
  { value: '99.5%', label: 'Inventory Accuracy', icon: <VerifiedIcon />, color: '#4CAF50' },
  { value: '24/7', label: 'Real-time Tracking', icon: <SpeedIcon />, color: '#FF9800' },
  { value: '100%', label: 'Data Security', icon: <SecurityIcon />, color: '#9C27B0' },
];

// Carousel images for car inventory management
const carouselImages = [
  {
    id: 1,
    imageUrl: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=1200&auto=format&fit=crop',
    title: 'Inventory Dashboard',
    backgroundColor: '#1976d2',
    description: 'Real-time vehicle inventory tracking and management',
    icon: <InventoryIcon sx={{ fontSize: 80 }} />,
  },
  {
    id: 2,
    imageUrl: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200&auto=format&fit=crop',
    title: 'Sales Analytics',
    backgroundColor: '#2e7d32',
    description: 'Track sales performance and profit margins',
    icon: <TrendingUpIcon sx={{ fontSize: 80 }} />,
  },
  {
    id: 3,
    imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop',
    title: 'Purchase Management',
    backgroundColor: '#ed6c02',
    description: 'Streamlined procurement and supplier management',
    icon: <ShoppingCartIcon sx={{ fontSize: 80 }} />,
  },
  {
    id: 4,
    imageUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&auto=format&fit=crop',
    title: 'Stock Optimization',
    backgroundColor: '#9c27b0',
    description: 'Smart inventory forecasting and stock alerts',
    icon: <StorefrontIcon sx={{ fontSize: 80 }} />,
  },
  {
    id: 5,
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&auto=format&fit=crop',
    title: 'Profit/Loss Analysis',
    backgroundColor: '#215E61',
    description: 'Comprehensive financial insights and reporting',
    icon: <WalletIcon sx={{ fontSize: 80 }} />,
  },
];

// Features for car inventory management
const features = [
  {
    title: 'Vehicle Inventory Tracking',
    description: 'Comprehensive tracking of all vehicle stock with real-time updates',
    icon: <InventoryIcon />,
    color: '#2196F3',
    points: ['Real-time stock levels', 'Vehicle specifications database', 'Location tracking', 'Stock alerts'],
  },
  {
    title: 'Purchase Information Management',
    description: 'Efficient procurement system for vehicle acquisitions',
    icon: <ShoppingCartIcon />,
    color: '#4CAF50',
    points: ['Supplier management', 'Purchase order tracking', 'Delivery scheduling', 'Cost analysis'],
  },
  {
    title: 'Sales InformationManagement',
    description: 'Complete sales tracking from inquiry to delivery',
    icon: <TrendingUpIcon />,
    color: '#FF9800',
    points: ['Sales pipeline tracking', 'Customer management', 'Invoice generation', 'Commission tracking'],
  },
  {
    title: 'Profit & Loss Analytics',
    description: 'Comprehensive financial analysis for each transaction',
    icon: <WalletIcon />,
    color: '#9C27B0',
    points: ['Real-time profit margins', 'Cost breakdown analysis', 'ROI calculations', 'Financial reporting'],
  },
  {
    title: 'Stock Report',
    description: 'Smart stock report to minimize carrying costs',
    icon: <StorefrontIcon />,
    color: '#673AB7',
    points: ['Demand forecasting', 'Stock level optimization', 'Turnover analysis', 'Auto-reorder alerts'],
  },
  {
    title: 'Multi-location Support',
    description: 'Manage inventory across multiple showrooms and warehouses',
    icon: <LocalShippingIcon />,
    color: '#00BCD4',
    points: ['Location-based tracking', 'Inter-warehouse transfers', 'Centralized reporting', 'Regional analytics'],
  },
];

// Process steps for car inventory management
const processSteps = [
  {
    step: 1,
    title: 'Purchase & Acquisition',
    description: 'Record vehicle purchases and supplier details',
    icon: <ShoppingCartIcon />,
    color: '#2196F3',
  },
  {
    step: 2,
    title: 'Inventory Management',
    description: 'Track stock levels and vehicle specifications',
    icon: <InventoryIcon />,
    color: '#4CAF50',
  },
  {
    step: 3,
    title: 'Sales & Delivery',
    description: 'Process sales and manage customer transactions',
    icon: <TrendingUpIcon />,
    color: '#FF9800',
  },
  {
    step: 4,
    title: 'Profit/Loss Analysis',
    description: 'Analyze financial performance and margins',
    icon: <WalletIcon />,
    color: '#9C27B0',
  },
];

const testimonials = [
  {
    name: 'Michael Chen',
    role: 'Operations Director',
    content: 'Sadaka Enterprise 1 transformed our inventory management. We now track vehicle movement with 99% accuracy.',
    avatar: 'MC',
  },
  {
    name: 'Sarah Williams',
    role: 'Sales Manager',
    content: 'The real-time profit/loss insights help us make better pricing decisions instantly.',
    avatar: 'SW',
  },
  {
    name: 'David Kumar',
    role: 'Finance Controller',
    content: 'Excellent reporting tools. We now have complete visibility of our margins across all vehicle types.',
    avatar: 'DK',
  },
];

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleDemoRequest = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setError('Demo request feature coming soon!');
    }, 1000);
  };

  // Carousel auto-play
  useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [autoPlay]);

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000); // Resume auto-play after 10 seconds
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000);
  };

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="info" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Header onLogin={handleLogin} />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 3, md: 4, lg: 5 },
          pb: 5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 780,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                    mb: 3,
                    color: 'primary.dark',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Sadaka Enterprise 1
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.secondary',
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    textAlign: 'justify',
                  }}
                >
                  Complete vehicle inventory management solution. Track purchases, sales, stock levels, costs, and profit/loss analytics for your automotive business.
                </Typography>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleLogin}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    }}
                  >
                    Login
                  </Button>
                </Stack>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Zoom in timeout={1200}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: { xs: 300, md: 400 },
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      '&:hover': {
                        '& .carousel-controls': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    {/* Carousel Slides */}
                    {carouselImages.map((slide, index) => (
                      <Box
                        key={slide.id}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: index === currentSlide ? 1 : 0,
                          transform: `translateX(${(index - currentSlide) * 100}%)`,
                          transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
                          background: `linear-gradient(135deg, ${slide.backgroundColor} 0%, ${alpha(slide.backgroundColor, 0.8)} 100%)`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          textAlign: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            animation: 'float 6s ease-in-out infinite',
                            '@keyframes float': {
                              '0%, 100%': { transform: 'translateY(0px)' },
                              '50%': { transform: 'translateY(-20px)' },
                            },
                            mb: 2,
                          }}
                        >
                          {slide.icon}
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            fontSize: { xs: '1.5rem', md: '2rem' },
                          }}
                        >
                          {slide.title}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            opacity: 0.9,
                            maxWidth: '85%',
                            fontSize: { xs: '0.95rem', md: '1.25rem' },
                            lineHeight: 1.4,
                          }}
                        >
                          {slide.description}
                        </Typography>
                      </Box>
                    ))}

                    {/* Navigation Dots */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        zIndex: 10,
                      }}
                    >
                      {carouselImages.map((_, index) => (
                        <IconButton
                          key={index}
                          onClick={() => handleDotClick(index)}
                          sx={{
                            p: 0.5,
                            color: index === currentSlide ? 'white' : alpha('#fff', 0.5),
                            '&:hover': {
                              color: 'white',
                            },
                          }}
                          size="small"
                        >
                          <CircleIcon
                            sx={{
                              fontSize: index === currentSlide ? 12 : 8,
                              transition: 'all 0.3s ease',
                            }}
                          />
                        </IconButton>
                      ))}
                    </Box>

                    {/* Navigation Buttons */}
                    <Box className="carousel-controls" sx={{ opacity: 0, transition: 'opacity 0.3s ease' }}>
                      <IconButton
                        onClick={handlePrevSlide}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: alpha('#000', 0.3),
                          color: 'white',
                          '&:hover': {
                            background: alpha('#000', 0.5),
                          },
                          zIndex: 10,
                        }}
                        size="large"
                      >
                        <ArrowBackIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleNextSlide}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: alpha('#000', 0.3),
                          color: 'white',
                          '&:hover': {
                            background: alpha('#000', 0.5),
                          },
                          zIndex: 10,
                        }}
                        size="large"
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </Box>

                    {/* Slide Counter */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        background: alpha('#000', 0.3),
                        color: 'white',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.875rem',
                        zIndex: 10,
                      }}
                    >
                      {currentSlide + 1} / {carouselImages.length}
                    </Box>
                  </Box>
                </Zoom>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box 
        sx={{ 
          py: { xs: 6, md: 10 }, 
          background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(118, 75, 162, 0.08) 0%, transparent 70%)',
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Slide direction="up" in timeout={600}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Trusted by Leading Automotive Dealers
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  fontWeight: 400,
                }}
              >
                Managing vehicle inventory with precision and real-time insights
              </Typography>
            </Box>
          </Slide>
          
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ rowGap: 7}}>
            {stats.map((stat, index) => (
              <Grid size={{ xs: 6, md: 3 }} key={index}>
                <Grow in timeout={800 + index * 200}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: { xs: 2.5, sm: 3, md: 4 },
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${stat.color}, ${alpha(stat.color, 0.6)})`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.4s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: `0 12px 40px ${alpha(stat.color, 0.2)}`,
                        borderColor: alpha(stat.color, 0.3),
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                        '& .stat-icon': {
                          transform: 'scale(1.15) rotate(10deg)',
                          background: stat.color,
                          boxShadow: `0 8px 24px ${alpha(stat.color, 0.3)}`,
                        },
                        '& .stat-value': {
                          transform: 'scale(1.08)',
                        }
                      },
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <Box
                      className="stat-icon"
                      sx={{
                        width: { xs: 50, sm: 60, md: 70 },
                        height: { xs: 50, sm: 60, md: 70 },
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(stat.color, 0.15)}, ${alpha(stat.color, 0.25)})`,
                        color: stat.color,
                        mx: 'auto',
                        mb: { xs: 2, md: 3 },
                        boxShadow: `0 4px 16px ${alpha(stat.color, 0.15)}`,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& svg': {
                          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' },
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        },
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Typography 
                      className="stat-value"
                      variant="h3" 
                      sx={{ 
                        fontWeight: 800, 
                        mb: { xs: 0.5, md: 1 }, 
                        color: stat.color,
                        fontSize: { xs: '2rem', sm: '2.25rem', md: '2.75rem' },
                        textShadow: `0 2px 8px ${alpha(stat.color, 0.2)}`,
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.1rem' },
                        lineHeight: 1.4,
                        fontWeight: 600,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    
                    {/* Decorative corner element */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -10,
                        right: -10,
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(stat.color, 0.08)} 0%, transparent 70%)`,
                        pointerEvents: 'none',
                      }}
                    />
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        sx={{ 
          py: { xs: 6, md: 10 }, 
          pb: { xs: 10, md: 12 },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 80% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  color: 'white',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                }}
              >
                Powerful Vehicle Management Features
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto',
                  color: 'rgba(255,255,255,0.95)',
                  fontWeight: 400,
                }}
              >
                Everything you need to manage your automotive inventory efficiently
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={{ xs: 3, md: 4 }} sx={{ rowGap: { xs: 10, md: 10, lg:10 }}}>
            {features.map((feature, index) => (
              <Grid size={{ xs:12, sm: 6, md: 4 }} key={index}>
                <Slide direction="up" in timeout={600 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      borderRadius: 4,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${feature.color}, ${alpha(feature.color, 0.6)})`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: `0 20px 60px ${alpha(feature.color, 0.25)}`,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(5deg)',
                          background: feature.color,
                          color: 'white',
                        }
                      },
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(feature.color, 0.1)}, ${alpha(feature.color, 0.2)})`,
                        color: feature.color,
                        mb: 3,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 4px 20px ${alpha(feature.color, 0.15)}`,
                      }}
                    >
                      {React.cloneElement(feature.icon, { sx: { fontSize: 32 } })}
                    </Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 700,
                        mb: 2,
                        background: `linear-gradient(135deg, #1a1a1a, ${feature.color})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      paragraph
                      sx={{ mb: 3, lineHeight: 1.7 }}
                    >
                      {feature.description}
                    </Typography>
                    <Stack spacing={1.5}>
                      {feature.points.map((point, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            opacity: 0,
                            animation: `fadeInUp 0.5s ease forwards ${0.1 * idx}s`,
                            '@keyframes fadeInUp': {
                              from: {
                                opacity: 0,
                                transform: 'translateY(10px)',
                              },
                              to: {
                                opacity: 1,
                                transform: 'translateY(0)',
                              }
                            }
                          }}
                        >
                          <CheckCircleIcon 
                            sx={{ 
                              color: feature.color, 
                              fontSize: 18, 
                              mr: 1.5,
                              flexShrink: 0,
                            }} 
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {point}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Flow Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 }, 
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '20%',
            left: '-10%',
            width: { xs: 200, md: 400 },
            height: { xs: 200, md: 400 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)',
            animation: 'pulse 8s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '10%',
            right: '-5%',
            width: { xs: 150, md: 300 },
            height: { xs: 150, md: 300 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(156, 39, 176, 0.08) 0%, transparent 70%)',
            animation: 'pulse 8s ease-in-out infinite 2s',
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(135deg, #2196F3 0%, #9C27B0 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Streamlined Vehicle Management Workflow
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  px: { xs: 2, md: 0 },
                }}
              >
                Efficient four-step process from acquisition to financial analysis
              </Typography>
            </Box>
          </Fade>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'flex-start' },
              gap: { xs: 0, md: 2 },
              position: 'relative',
              px: { xs: 2, sm: 3, md: 0 },
            }}
          >
            {/* Desktop connecting line */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 0, md: 70 },
                left: { xs: '50%', md: '12%' },
                right: { xs: 'auto', md: '12%' },
                bottom: { xs: 0, md: 'auto' },
                width: { xs: 3, md: 'auto' },
                height: { xs: 'auto', md: 3 },
                transform: { xs: 'translateX(-50%)', md: 'none' },
                background: { 
                  xs: `linear-gradient(180deg, 
                    ${alpha('#2196F3', 0.3)} 0%, 
                    ${alpha('#4CAF50', 0.3)} 33%, 
                    ${alpha('#FF9800', 0.3)} 66%, 
                    ${alpha('#9C27B0', 0.3)} 100%)`,
                  md: `linear-gradient(90deg, 
                    ${alpha('#2196F3', 0.3)} 0%, 
                    ${alpha('#4CAF50', 0.3)} 33%, 
                    ${alpha('#FF9800', 0.3)} 66%, 
                    ${alpha('#9C27B0', 0.3)} 100%)`
                },
                zIndex: 0,
              }}
            />

            {processSteps.map((step, index) => (
              <Zoom in timeout={800 + index * 200} key={step.step}>
                <Box
                  sx={{
                    flex: { xs: 'none', md: 1 },
                    textAlign: 'center',
                    p: { xs: 3, sm: 3.5, md: 3 },
                    position: 'relative',
                    zIndex: 1,
                    mb: { xs: index !== processSteps.length - 1 ? 4 : 0, md: 0 },
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: alpha(step.color, 0.2),
                    boxShadow: `0 4px 20px ${alpha(step.color, 0.1)}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: { xs: 'scale(1.02)', md: 'translateY(-12px) scale(1.03)' },
                      boxShadow: `0 12px 40px ${alpha(step.color, 0.25)}`,
                      borderColor: alpha(step.color, 0.4),
                      '& .process-icon': {
                        transform: 'scale(1.15) rotate(5deg)',
                        boxShadow: `0 12px 32px ${alpha(step.color, 0.5)}`,
                      },
                      '& .step-chip': {
                        transform: 'scale(1.1)',
                        background: step.color,
                        color: 'white',
                      }
                    },
                  }}
                >
                  {/* Step number badge - top right */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: { xs: -12, md: -16 },
                      right: { xs: -12, md: -16 },
                      width: { xs: 32, md: 40 },
                      height: { xs: 32, md: 40 },
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${step.color} 0%, ${alpha(step.color, 0.8)} 100%)`,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      boxShadow: `0 4px 16px ${alpha(step.color, 0.4)}`,
                      border: '3px solid white',
                    }}
                  >
                    {step.step}
                  </Box>

                  {/* Icon */}
                  <Box
                    className="process-icon"
                    sx={{
                      width: { xs: 70, sm: 80, md: 90 },
                      height: { xs: 70, sm: 80, md: 90 },
                      borderRadius: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${alpha(step.color, 0.15)} 0%, ${alpha(step.color, 0.25)} 100%)`,
                      color: step.color,
                      mx: 'auto',
                      mb: { xs: 2.5, md: 3 },
                      boxShadow: `0 8px 24px ${alpha(step.color, 0.25)}`,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& svg': {
                        fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                      },
                    }}
                  >
                    {step.icon}
                  </Box>

                  {/* Step label chip */}
                  <Chip
                    className="step-chip"
                    label={`Step ${step.step}`}
                    sx={{
                      mb: 2,
                      background: alpha(step.color, 0.1),
                      color: step.color,
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      height: { xs: 28, md: 32 },
                      transition: 'all 0.3s ease',
                      border: `1px solid ${alpha(step.color, 0.3)}`,
                    }}
                  />

                  {/* Title */}
                  <Typography 
                    variant="h5" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                      mb: { xs: 1.5, md: 2 },
                      color: step.color,
                    }}
                  >
                    {step.title}
                  </Typography>

                  {/* Description */}
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                      lineHeight: 1.6,
                      px: { xs: 1, md: 0 },
                    }}
                  >
                    {step.description}
                  </Typography>

                  {/* Decorative bottom element */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, transparent 0%, ${step.color} 50%, transparent 100%)`,
                      opacity: 0.3,
                      borderBottomLeftRadius: 16,
                      borderBottomRightRadius: 16,
                    }}
                  />
                </Box>
              </Zoom>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-10%',
            width: { xs: 300, md: 600 },
            height: { xs: 300, md: 600 },
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
            animation: 'float 20s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
              '50%': { transform: 'translate(50px, 50px) scale(1.1)' },
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            right: '-5%',
            width: { xs: 250, md: 500 },
            height: { xs: 250, md: 500 },
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
            animation: 'float 15s ease-in-out infinite 5s',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box 
              sx={{ 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: 6,
                p: { xs: 4, sm: 6, md: 8 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              }}
            >
              {/* Decorative icon */}
              <Box
                sx={{
                  width: { xs: 60, md: 80 },
                  height: { xs: 60, md: 80 },
                  borderRadius: '24px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  animation: 'bounce 2s ease-in-out infinite',
                  '@keyframes bounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-10px)' },
                  },
                  '& svg': {
                    fontSize: { xs: '2rem', md: '2.5rem' },
                  }
                }}
              >
                <RocketLaunchIcon />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  color: 'white',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  lineHeight: 1.2,
                }}
              >
                Ready to Optimize Your Vehicle Inventory?
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 5, 
                  mx: 'auto', 
                  maxWidth: 600,
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  px: { xs: 2, sm: 0 },
                }}
              >
                Join leading automotive dealers using Sadaka Enterprise 1 to streamline their operations and maximize profits
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={{ xs: 2, sm: 3 }} 
                justifyContent="center"
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: { xs: 4, sm: 6, md: 8 },
                    py: { xs: 1.75, md: 2 },
                    borderRadius: 3,
                    fontWeight: 700,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    background: 'white',
                    color: '#667eea',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    textTransform: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'white',
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.4)',
                    },
                  }}
                >
                  Get Started Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleDemoRequest}
                  disabled={loading}
                  endIcon={loading ? null : <EmailIcon />}
                  sx={{
                    px: { xs: 4, sm: 6, md: 8 },
                    py: { xs: 1.75, md: 2 },
                    borderRadius: 3,
                    fontWeight: 700,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    borderColor: 'white',
                    color: 'white',
                    borderWidth: 2,
                    textTransform: 'none',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'white',
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 8px 32px rgba(255, 255, 255, 0.3)',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Contact Us'}
                </Button>
              </Stack>

              {/* Trust indicators */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 4 },
                  pt: 4,
                  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                  <CheckCircleIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500 }}>
                    Free Demo Available
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                  <SecurityIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500 }}>
                    Secure & Compliant
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                  <SupportAgentIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500 }}>
                    24/7 Support
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LandingPage;